import { useEffect, useRef } from "react";

// Fond animé du hero : graphique de cours qui défile (grille de prix, moyenne mobile,
// volumes calés juste au-dessus des cartes du hero, étiquette du prix courant).
// Purement décoratif : aria-hidden, aucune donnée réelle.

const GREEN = "0,191,99"; // #00BF63
const ORANGE = "247,152,55"; // #F79837
const GREY = "113,113,122"; // zinc-500
const MASK = "linear-gradient(90deg, rgba(0,0,0,.18) 0%, rgba(0,0,0,.4) 40%, #000 72%)";

const STEP = 5; // espacement des points (px)
const VOL = 0.085; // volatilité de la courbe
const BASE = 0.55; // niveau moyen
const SPEED = 0.016; // vitesse de défilement (px par ms)

export default function HeroChartBackground({ anchorRef }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    const hero = cv?.parentElement;
    if (!cv || !hero) return;
    const ctx = cv.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0, H = 0, raf = 0, last = 0, off = 0;
    let vals = [], vols = [];
    const clamp = (x) => Math.max(0.03, Math.min(0.97, x));
    const next = (l) => clamp(l + (Math.random() - 0.5) * VOL + (BASE - l) * 0.02);

    const resize = () => {
      const r = hero.getBoundingClientRect();
      const d = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height;
      cv.width = W * d; cv.height = H * d;
      ctx.setTransform(d, 0, 0, d, 0, 0);
      const n = Math.ceil(W / STEP) + 30;
      if (vals.length < n) {
        if (!vals.length) vals = [BASE];
        while (vals.length < n) vals.push(next(vals[vals.length - 1]));
        while (vols.length < vals.length) vols.push(Math.random());
      }
    };

    const path = (top, h) => {
      ctx.beginPath();
      vals.forEach((v, i) => {
        const x = i * STEP - off, y = top + (1 - v) * h;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      });
    };
    const yAt = (x, top, h) => {
      const i = Math.min(vals.length - 1, Math.max(0, Math.round((x + off) / STEP)));
      return top + (1 - vals[i]) * h;
    };

    const draw = (dt) => {
      off += dt * SPEED;
      while (off >= STEP) {
        off -= STEP;
        vals.shift(); vals.push(next(vals[vals.length - 1]));
        vols.shift(); vols.push(Math.random());
      }
      ctx.clearRect(0, 0, W, H);

      // Zones : graphique en haut, volumes collés au-dessus des cartes (anchorRef)
      const heroTop = hero.getBoundingClientRect().top;
      // Téléphone : les volumes sont posés sur le bas de l'en-tête ; ordinateur : juste au-dessus des cartes
      const anchorTop = W < 640
        ? H + 4
        : anchorRef?.current
          ? anchorRef.current.getBoundingClientRect().top - heroTop
          : H * 0.85;
      const vh = Math.min(70, H * 0.09);
      const vb = anchorTop - 4 - vh;
      const top = H * 0.08;
      const h = Math.max(120, vb - 18 - top);

      // Téléphone : sans les prix ni l'étiquette, qui passeraient sous le texte
      const small = W < 640;
      // Grille horizontale + prix
      ctx.font = '11px "IBM Plex Mono", monospace';
      ctx.textAlign = "right";
      for (let i = 0; i <= 6; i++) {
        const y = top + (h * i) / 6;
        ctx.strokeStyle = `rgba(${GREY},.12)`;
        ctx.setLineDash([2, 6]);
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W - 70, y); ctx.stroke();
        ctx.setLineDash([]);
        if (small) continue;
        ctx.fillStyle = `rgba(${GREY},.55)`;
        ctx.fillText((68400 - i * 600).toLocaleString("fr-FR"), W - 14, y + 4);
      }
      // Grille verticale qui défile
      for (let x = W - ((performance.now() / 40) % 120); x > 0; x -= 120) {
        ctx.strokeStyle = `rgba(${GREY},.06)`;
        ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, vb + vh); ctx.stroke();
      }
      // Volumes
      vals.forEach((v, i) => {
        const x = i * STEP - off;
        const up = i > 0 && v >= vals[i - 1];
        ctx.fillStyle = up ? `rgba(${GREEN},.28)` : `rgba(${GREY},.22)`;
        const bh = vols[i] * vh;
        ctx.fillRect(x, vb + vh - bh, STEP - 1.5, bh);
      });
      // Moyenne mobile (orange, pointillés)
      ctx.beginPath();
      vals.forEach((_, i) => {
        const a = vals.slice(Math.max(0, i - 20), i + 1);
        const m = a.reduce((p, c) => p + c, 0) / a.length;
        const x = i * STEP - off, y = top + (1 - m) * h;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      });
      ctx.strokeStyle = `rgba(${ORANGE},.55)`;
      ctx.lineWidth = 1.2; ctx.setLineDash([5, 5]); ctx.stroke(); ctx.setLineDash([]);
      // Courbe + remplissage
      path(top, h);
      ctx.strokeStyle = `rgba(${GREEN},1)`; ctx.lineWidth = 2; ctx.stroke();
      ctx.lineTo(W + 30, top + h); ctx.lineTo(0, top + h); ctx.closePath();
      const gr = ctx.createLinearGradient(0, top, 0, top + h);
      gr.addColorStop(0, `rgba(${GREEN},.2)`); gr.addColorStop(1, `rgba(${GREEN},0)`);
      ctx.fillStyle = gr; ctx.fill();
      // Prix courant : ligne pointillée, point lumineux, étiquette
      const xd = W - 90, y = yAt(xd, top, h);
      const price = Math.round(68400 - ((y - top) / h) * 3600);
      ctx.strokeStyle = `rgba(${GREEN},.5)`; ctx.setLineDash([3, 4]);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W - 70, y); ctx.stroke(); ctx.setLineDash([]);
      const p = 0.5 + 0.5 * Math.sin(performance.now() / 500);
      ctx.beginPath(); ctx.arc(xd, y, 3 + p * 2, 0, 7);
      ctx.fillStyle = `rgba(${GREEN},.95)`; ctx.shadowColor = `rgba(${GREEN},1)`; ctx.shadowBlur = 16;
      ctx.fill(); ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(xd, y, 9 + p * 8, 0, 7);
      ctx.strokeStyle = `rgba(${GREEN},${0.35 * (1 - p)})`; ctx.lineWidth = 1; ctx.stroke();
      if (small) return;
      ctx.fillStyle = `rgba(${GREEN},1)`; ctx.fillRect(W - 68, y - 10, 62, 20);
      ctx.fillStyle = "#04140d"; ctx.font = '600 11px "IBM Plex Mono", monospace';
      ctx.fillText(price.toLocaleString("fr-FR"), W - 12, y + 4);
    };

    const loop = (now) => {
      const dt = Math.min(64, now - (last || now));
      last = now;
      draw(dt);
      raf = requestAnimationFrame(loop);
    };

    resize();
    const ro = new ResizeObserver(() => { resize(); if (reduce) draw(0); });
    ro.observe(hero);
    if (reduce) draw(0); else raf = requestAnimationFrame(loop);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [anchorRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="hero-chart"
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none",
        opacity: 0.55, WebkitMaskImage: MASK, maskImage: MASK,
      }}
    />
  );
}
